import { Pencil, Trash2, Copy } from "lucide-react";
import { Link } from "react-router-dom";

const TYPE_STYLES = {
  flat_rate: "border-l-2 border-amber-400",
  unit_based: "border-l-2 border-teal-400",
};

const TYPE_BADGE = {
  flat_rate: { label: "Flat", cls: "bg-amber-100 text-amber-700" },
  unit_based: { label: "Unit", cls: "bg-teal-100 text-teal-700" },
};

/* helpers - module scope so the export columns below can reuse them */
const formatLanguagePair = (lp) => {
  if (!lp) return "—";
  const src = lp.sourceLanguage?.name || lp.sourceLanguage?.code || "";
  const tgt = lp.targetLanguage?.name || lp.targetLanguage?.code || "";
  return src && tgt ? `${src} → ${tgt}` : "—";
};

const formatCurrency = (c) => c?.currency?.code || "—";

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return "—";
  }
};

const fmt = (v, fallback = "—") =>
  v !== undefined && v !== null && v !== "" ? v : fallback;

const fmtNum = (v, fallback = "0.00") =>
  v !== undefined && v !== null && v !== "" ? v : fallback;

/* project + client info */
const getProjectInfo = (row) => {
  const client = row.project?.client;

  const clientName = client
    ? client.type?.toLowerCase() === "company"
      ? client.company_name || "—"
      : client.primary_user
      ? `${client.primary_user.first_name} ${client.primary_user.last_name}`.trim()
      : "—"
    : "—";

  return {
    id: row.project?.id || "—",
    name: row.project?.project_name || "—",
    deadline: row.project?.deadline_at || "—",
    status: row.project?.status || "—",
    client: clientName,
    clientId: client?.id || null,
  };
};

// Same columns the table renders, minus the checkbox and actions.
export const RECEIVABLES_EXPORT_COLUMNS = [
  { header: "Type", value: (r) => (TYPE_BADGE[r.type] ?? TYPE_BADGE.flat_rate).label },
  { header: "Code", value: (r) => r.receivable_code || "" },
  { header: "Invoice", value: (r) => r.invoice?.invoice_code || "" },
  { header: "Project Client", value: (r) => getProjectInfo(r).client },
  { header: "PO Number", value: (r) => fmt(r.po_number, "") },
  { header: "Service", value: (r) => r.service?.name || "" },
  { header: "Language Pair", value: (r) => formatLanguagePair(r.languagePair) },
  {
    header: "Unit Amount",
    value: (r) => (r.type === "flat_rate" ? "" : fmt(r.unit_amount, "")),
  },
  { header: "Unit", value: (r) => (r.type === "flat_rate" ? "" : r.unit?.name || "") },
  {
    header: "Price / Unit",
    value: (r) => (r.type === "flat_rate" ? "" : fmt(r.price_per_unit, "")),
  },
  { header: "Total", value: (r) => fmtNum(r.subtotal) },
  { header: "Currency", value: (r) => formatCurrency(r.currency) },
  { header: "Project", value: (r) => getProjectInfo(r).id },
  { header: "Project Name", value: (r) => getProjectInfo(r).name },
  { header: "Deadline", value: (r) => formatDate(getProjectInfo(r).deadline) },
  { header: "Status", value: (r) => getProjectInfo(r).status },
];

const ReceivablesTable = ({
  data = [],
  onEdit,
  onDelete,
  onClone,
  readOnly = false,
  selectedIds = [],
  onToggle,
  onToggleAll,
}) => {

  const getStatusBadgeColor = (status) => {
    const statusMap = {
      active: "bg-green-100 text-green-700",
      completed: "bg-brand-100 text-brand-700",
      "in progress": "bg-brand-100 text-brand-700",
      "on-hold": "bg-warning-100 text-warning-700",
      cancelled: "bg-danger-100 text-danger-700",
    };
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-700";
  };

  /* select all state (only eligible = not yet invoiced rows count) */
  const eligible = data.filter((r) => !r.invoice);
  const allSelected = eligible.length > 0 && eligible.every((r) => selectedIds.includes(`${r.type}-${r.id}`));
  const someSelected = data.some((r) => selectedIds.includes(`${r.type}-${r.id}`));

  /* columns */
  const HEADERS = [
    "checkbox",
    "Type",
    "Code",
    "Invoice",
    "Project Client",
    "PO Number",
    "Service",
    "Language Pair",
    "Unit Amount",
    "Unit",
    "Price / Unit",
    "Total",
    "Currency",
    "Project",
    "Project Name",
    "Deadline",
    "Status",
    "Actions",
  ];

  const COL_WIDTHS = [
    "2%",  // Checkbox
    "3%",  // Type
    "4%",  // Code
    "4%",  // Invoice
    "8%",  // Project Client
    "6%",  // PO Number
    "8%",  // Service
    "9%",  // Language Pair
    "5%",  // Unit Amount
    "4%",  // Unit
    "5%",  // Price / Unit
    "4%",  // Total
    "4%",  // Currency
    "5%",  // Project
    "8%",  // Project Name
    "8%",  // Deadline
    "7%",  // Status
    "7%",  // Actions
  ];

  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-b bg-gray-50 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-3 rounded-sm bg-amber-400" />
          Flat Rate
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-3 rounded-sm bg-teal-400" />
          Unit Based
        </span>
        {selectedIds.length > 0 && (
          <span className="ml-auto text-brand-600 font-medium">
            {selectedIds.length} selected
          </span>
        )}
      </div>

      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table className="w-full table-auto border-collapse min-w-[1600px]">
          <colgroup>
            {COL_WIDTHS.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>

          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              {/* Select All checkbox */}
              <th className="px-3 py-2 text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => onToggleAll?.(eligible)}
                  className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                />
              </th>
              {HEADERS.slice(1).map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={HEADERS.length}
                  className="py-10 text-center text-gray-400 text-xs"
                >
                  No receivables found.
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const rowKey = `${row.type}-${row.id}`;
                const isInvoiced = !!row.invoice;
                const isSelected = selectedIds.includes(rowKey);
                const isFlat = row.type === "flat_rate";
                const badge = TYPE_BADGE[row.type] ?? TYPE_BADGE.flat_rate;
                const typeStyle = TYPE_STYLES[row.type] ?? "";
                const rowBg = isInvoiced
                  ? "bg-gray-100 opacity-70"
                  : isSelected
                  ? "bg-brand-50"
                  : index % 2 === 0
                  ? "bg-gray-50"
                  : "bg-white";
                const projectInfo = getProjectInfo(row);
                const statusBadge = getStatusBadgeColor(projectInfo.status);

                return (
                  <tr
                    key={rowKey}
                    className={`${rowBg} ${typeStyle} hover:bg-brand-50 transition-colors`}
                  >
                    {/* Checkbox */}
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isInvoiced}
                        onChange={() => !isInvoiced && onToggle?.(rowKey)}
                        className="w-3.5 h-3.5 accent-brand-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                      />
                    </td>

                    {/* Type badge */}
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span
                        className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}
                      >
                        {badge.label}
                      </span>
                    </td>

                    {/* Code */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {row.id ? (
                        <Link
                          to={`/client/receivable/${row.id}?type=${row.type}`}
                          className="text-brand-600 hover:text-brand-800 underline font-medium"
                        >
                          {row.receivable_code || "—"}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* Invoice */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                      {row.invoice ? (
                        <Link
                          to={`/client/invoice/${row.invoice.id}`}
                          className="text-brand-600 hover:underline font-medium"
                        >
                          {row.invoice.invoice_code}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* Project Client */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {projectInfo.clientId ? (
                        <Link
                          to={`/clients/${projectInfo.clientId}`}
                          className="text-brand-600 hover:text-brand-800 underline font-medium"
                        >
                          {projectInfo.client}
                        </Link>
                      ) : (
                        projectInfo.client
                      )}
                    </td>

                    {/* PO Number */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {fmt(row.po_number)}
                    </td>

                    {/* Service */}
                    <td className="px-3 py-2 text-xs break-words">
                      {row.service?.name || "—"}
                    </td>

                    {/* Language Pair */}
                    <td className="px-3 py-2 text-xs break-words">
                      {formatLanguagePair(row.languagePair)}
                    </td>

                    {/* Unit Amount - only unit_based */}
                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {isFlat ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        fmt(row.unit_amount)
                      )}
                    </td>

                    {/* Unit - only unit_based */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {isFlat ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        row.unit?.name || "—"
                      )}
                    </td>

                    {/* Price per Unit - only unit_based */}
                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {isFlat ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        fmt(row.price_per_unit)
                      )}
                    </td>

                    {/* Total / Subtotal */}
                    <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">
                      {fmtNum(row.subtotal)}
                    </td>

                    {/* Currency */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {formatCurrency(row.currency)}
                    </td>

                    {/* Project ID */}
                    <td className="px-3 py-2 text-xs text-brand-600 whitespace-nowrap">
                      {projectInfo.id}
                    </td>

                    {/* Project Name */}
                    <td className="px-3 py-2 text-xs break-words">
                      {projectInfo.name}
                    </td>

                    {/* Project Deadline */}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {formatDate(projectInfo.deadline)}
                    </td>

                    {/* Project Status */}
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span
                        className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded capitalize ${statusBadge}`}
                      >
                        {projectInfo.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {readOnly ? (
                        <span className="text-[10px] text-gray-400">—</span>
                      ) : (
                        <div className="flex justify-center gap-3">
                          <Copy
                            className="w-4 h-4 text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
                            title="Clone"
                            onClick={() => onClone?.(row)}
                          />
                          <Pencil
                            className={`w-4 h-4 transition-colors ${
                              isInvoiced
                                ? "text-gray-200 cursor-not-allowed"
                                : "text-brand-400 hover:text-brand-600 cursor-pointer"
                            }`}
                            title={isInvoiced ? "Cannot edit an invoiced receivable" : "Edit"}
                            onClick={() => !isInvoiced && onEdit?.(row)}
                          />
                          <Trash2
                            className={`w-4 h-4 transition-colors ${
                              isInvoiced
                                ? "text-gray-200 cursor-not-allowed"
                                : "text-danger-400 hover:text-danger-600 cursor-pointer"
                            }`}
                            title={isInvoiced ? "Cannot delete an invoiced receivable" : "Delete"}
                            onClick={() => !isInvoiced && onDelete?.(row)}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReceivablesTable;